import React, { useState, useEffect } from 'react';
import { getSurfForecast } from './api';
import { Search, Sun, Moon, Heart } from 'lucide-react';

/* ─── Theme CSS ──────────────────────────────────────────────────────────── */
const themeStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=DM+Sans:wght@300;400;500&display=swap');

  :root {
    --bg-page:        #f0f4f8;
    --bg-card:        #ffffff;
    --bg-inner:       rgba(255,255,255,0.6);
    --bg-inner2:      rgba(255,255,255,0.5);
    --border:         rgba(0,0,0,0.08);
    --text-primary:   #1a2540;
    --text-secondary: #4a5578;
    --text-muted:     #8a95b0;
    --header-bg:      linear-gradient(135deg, #e8f0ff 0%, #dbeafe 100%);
    --shadow:         0 2px 12px rgba(0,0,0,0.08);
    --tab-active-bg:  #3b82f6;
    --tab-active-txt: #ffffff;
    --tab-bg:         rgba(255,255,255,0.7);
    --tab-txt:        #4a5578;
    --search-bg:      #ffffff;
    --search-border:  rgba(0,0,0,0.15);
    --dropdown-bg:    #ffffff;
    --loader-color:   #3b82f6;
    --location-bg:    rgba(255,255,255,0.7);
    --toggle-bg:      rgba(0,0,0,0.08);
    --toggle-hover:   rgba(0,0,0,0.13);
  }

  [data-theme="dark"] {
    --bg-page:        #07080d;
    --bg-card:        rgba(255,255,255,0.04);
    --bg-inner:       rgba(0,0,0,0.25);
    --bg-inner2:      rgba(0,0,0,0.2);
    --border:         rgba(255,255,255,0.09);
    --text-primary:   #dde5ff;
    --text-secondary: #9aaad0;
    --text-muted:     #5a6888;
    --header-bg:      linear-gradient(135deg, #0d1020 0%, #0a1530 100%);
    --shadow:         0 2px 16px rgba(0,0,0,0.5);
    --tab-active-bg:  rgba(60,110,220,0.35);
    --tab-active-txt: #a8c4ff;
    --tab-bg:         rgba(255,255,255,0.04);
    --tab-txt:        #7a90b8;
    --search-bg:      rgba(255,255,255,0.06);
    --search-border:  rgba(255,255,255,0.12);
    --dropdown-bg:    #131520;
    --loader-color:   #6090ff;
    --location-bg:    rgba(255,255,255,0.04);
    --toggle-bg:      rgba(255,255,255,0.08);
    --toggle-hover:   rgba(255,255,255,0.14);
  }

  * { box-sizing: border-box; }

  .surf-app {
    min-height: 100vh;
    background: var(--bg-page);
    color: var(--text-primary);
    font-family: 'DM Sans', sans-serif;
    transition: background 0.25s, color 0.25s;
  }

  .surf-header {
    background: var(--header-bg);
    border-bottom: 1px solid var(--border);
    padding: 36px 24px 28px;
    text-align: center;
    position: relative;
  }

  .theme-toggle {
    position: absolute;
    top: 20px;
    right: 20px;
    background: var(--toggle-bg);
    border: 1px solid var(--border);
    border-radius: 8px;
    color: var(--text-secondary);
    cursor: pointer;
    padding: 7px 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s;
  }
  .theme-toggle:hover { background: var(--toggle-hover); }

  .surf-title {
    font-family: 'Playfair Display', serif;
    font-size: clamp(1.9rem, 5vw, 3rem);
    font-weight: 700;
    color: var(--text-primary);
    margin-bottom: 4px;
  }

  .surf-subtitle {
    font-size: 12px;
    color: var(--text-muted);
    letter-spacing: 0.1em;
    text-transform: uppercase;
    margin-bottom: 24px;
  }

  .search-wrap { position: relative; max-width: 420px; margin: 0 auto; z-index: 20; }

  .search-input {
    width: 100%;
    background: var(--search-bg);
    border: 1px solid var(--search-border);
    border-radius: 10px;
    color: var(--text-primary);
    font-family: 'DM Sans', sans-serif;
    font-size: 15px;
    padding: 11px 16px 11px 42px;
    outline: none;
    transition: border-color 0.2s;
  }
  .search-input::placeholder { color: var(--text-muted); }
  .search-input:focus { border-color: #3b82f6; }

  .search-icon {
    position: absolute;
    left: 13px; top: 50%;
    transform: translateY(-50%);
    color: var(--text-muted);
    width: 17px; height: 17px;
  }

  .search-dropdown {
    position: absolute;
    top: calc(100% + 6px);
    left: 0; right: 0;
    background: var(--dropdown-bg);
    border: 1px solid var(--border);
    border-radius: 10px;
    overflow: hidden;
    box-shadow: var(--shadow);
    z-index: 100;
  }

  .search-section-label {
    padding: 8px 14px 5px;
    font-size: 10px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--text-muted);
    border-bottom: 1px solid var(--border);
  }

  .search-item {
    display: block; width: 100%; text-align: left;
    background: none; border: none;
    padding: 10px 14px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    color: var(--text-secondary);
    cursor: pointer;
    border-bottom: 1px solid var(--border);
    transition: background 0.15s;
  }
  .search-item:hover { background: rgba(59,130,246,0.08); color: var(--text-primary); }
  .search-item-fav { display: flex; align-items: center; justify-content: space-between; padding: 0; border-bottom: 1px solid var(--border); }
  .search-item-label { display: flex; align-items: center; flex: 1; background: none; border: none; padding: 10px 14px; text-align: left; font-size: 13px; color: var(--text-secondary); cursor: pointer; transition: background 0.15s; }
  .search-item-label:hover { background: rgba(59,130,246,0.08); color: var(--text-primary); }
  .search-item-remove { background: none; border: none; padding: 10px 14px; font-size: 16px; color: var(--text-muted); cursor: pointer; line-height: 1; }
  .search-item-remove:hover { color: #ef4444; }

  .location-bar {
    background: var(--location-bg);
    border-bottom: 1px solid var(--border);
    padding: 14px 24px;
    text-align: center;
    display: flex; flex-direction: column; align-items: center;
  }
  .location-name-row { display: flex; align-items: center; gap: 6px; }
  .location-name {
    font-family: 'Playfair Display', serif;
    font-size: clamp(1.1rem, 3vw, 1.5rem);
    color: var(--text-primary);
  }
  .location-coords { font-size: 12px; color: var(--text-muted); margin-top: 2px; }

  .main-content { max-width: 1100px; margin: 0 auto; padding: 24px 20px 60px; }

  .loading-wrap { display: flex; flex-direction: column; align-items: center; padding: 80px 0; gap: 16px; }
  .wave-loader { display: flex; gap: 5px; align-items: flex-end; height: 36px; }
  .wave-bar {
    width: 5px; border-radius: 3px;
    background: var(--loader-color);
    animation: wavePulse 1.2s ease-in-out infinite;
  }
  .wave-bar:nth-child(1){animation-delay:0s}
  .wave-bar:nth-child(2){animation-delay:.15s}
  .wave-bar:nth-child(3){animation-delay:.3s}
  .wave-bar:nth-child(4){animation-delay:.45s}
  .wave-bar:nth-child(5){animation-delay:.6s}
  @keyframes wavePulse {
    0%,100%{height:8px;opacity:.4}
    50%{height:32px;opacity:1}
  }
  .loading-text { font-size: 13px; color: var(--text-muted); letter-spacing: 0.08em; text-transform: uppercase; }

  .overview-section { margin: 8px 0 24px; }
  .day-heading {
    font-family: 'Playfair Display', serif;
    font-size: 1.15rem;
    color: var(--text-secondary);
    margin-bottom: 16px;
  }
  .day-heading strong { color: var(--text-primary); }

  .overview-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 14px;
  }
  @media(max-width:680px){ .overview-grid{ grid-template-columns:1fr; } }

  .card {
    background: var(--bg-card);
    border: 1px solid var(--border);
    border-radius: 14px;
    overflow: hidden;
    box-shadow: var(--shadow);
    transition: transform 0.2s, box-shadow 0.2s;
  }
  .card:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(0,0,0,0.12); }
  .card-inner { padding: 22px 20px; }

  .card-label {
    font-size: 11px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--text-muted);
    margin-bottom: 12px;
    text-align: center;
  }

  .card-icon { font-size: 36px; text-align: center; margin-bottom: 8px; display: block; }

  .card-verdict {
    font-family: 'Playfair Display', serif;
    font-size: 1.35rem;
    font-weight: 700;
    text-align: center;
    margin-bottom: 4px;
  }

  .card-desc { font-size: 13px; color: var(--text-secondary); text-align: center; margin-bottom: 14px; }

  .inner-box {
    background: var(--bg-inner);
    border-radius: 9px;
    padding: 12px 14px;
    border: 1px solid var(--border);
  }

  .stats-2col { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; text-align: center; }

  .stat-label { font-size: 12px; color: var(--text-muted); margin-bottom: 3px; }
  .stat-value { font-size: 15px; font-weight: 600; }
  .stat-sub   { font-size: 11px; color: var(--text-muted); margin-top: 2px; }

  .day-tabs {
    display: flex; gap: 8px; overflow-x: auto;
    padding: 4px 0 14px; scrollbar-width: none; margin-bottom: 4px;
  }
  .day-tabs::-webkit-scrollbar { display: none; }

  .day-tab {
    flex-shrink: 0;
    background: var(--tab-bg);
    border: 1px solid var(--border);
    border-radius: 10px;
    color: var(--tab-txt);
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    padding: 9px 18px;
    font-size: 14px;
    text-align: center;
    min-width: 68px;
    transition: all 0.18s;
  }
  .day-tab:hover { color: var(--text-primary); }
  .day-tab.active {
    background: var(--tab-active-bg);
    border-color: transparent;
    color: var(--tab-active-txt);
  }
  .tab-day  { font-weight: 500; }
  .tab-date { font-size: 11px; opacity: 0.7; margin-top: 2px; }

  .time-grid {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 12px;
  }
  @media(max-width:900px){ .time-grid{ grid-template-columns:repeat(3,1fr); } }
  @media(max-width:560px){ .time-grid{ grid-template-columns:repeat(2,1fr); } }

  .time-card {
    border-radius: 14px;
    overflow: hidden;
    box-shadow: var(--shadow);
    transition: transform 0.2s;
    border: 1px solid var(--border);
  }
  .time-card:hover { transform: translateY(-3px); }
  .time-card-inner { padding: 16px; }

  .tc-time-label {
    font-size: 13px;
    font-weight: 500;
    color: var(--text-secondary);
    text-align: center;
    margin-bottom: 8px;
  }

  .wave-dots { display: flex; gap: 4px; justify-content: center; margin: 6px 0 10px; }
  .wave-dot  { width: 8px; height: 8px; border-radius: 50%; background: #3b82f6; }

  .tc-condition {
    font-size: 20px;
    font-weight: 700;
    text-align: center;
    margin-bottom: 2px;
  }
  .tc-desc { font-size: 12px; color: var(--text-secondary); text-align: center; margin-bottom: 12px; }

  .tc-inner-box {
    background: var(--bg-inner2);
    border-radius: 8px;
    padding: 10px 12px;
    border: 1px solid var(--border);
  }

  .tc-stats-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; text-align: center; }
  .tc-stat-label { font-size: 12px; color: var(--text-muted); margin-bottom: 3px; }
  .tc-stat-value { font-size: 14px; font-weight: 600; }
  .tc-stat-sub   { font-size: 11px; color: var(--text-muted); margin-top: 1px; }

  .divider { height: 1px; background: var(--border); margin: 8px 0; }

  .error-box {
    background: rgba(220,60,60,0.1);
    border: 1px solid rgba(220,60,60,0.25);
    border-radius: 10px;
    padding: 16px 20px;
    color: #e05555;
    font-size: 14px;
    margin: 20px 0;
  }

  .surf-cam-card {
    max-width: 860px; margin: 0 auto 20px; padding: 0 16px;
  }
  .surf-cam-inner {
    display: flex; align-items: center; justify-content: space-between;
    background: var(--card-bg); border: 1px solid var(--border);
    border-left: 4px solid #3b82f6;
    border-radius: 12px; padding: 14px 20px; gap: 16px;
  }
  .surf-cam-info { display: flex; align-items: center; gap: 12px; }
  .surf-cam-icon { font-size: 24px; line-height: 1; }
  .surf-cam-title { font-size: 14px; font-weight: 700; color: var(--text-primary); margin-bottom: 2px; }
  .surf-cam-sub   { font-size: 12px; color: var(--text-muted); }
  .surf-cam-btn {
    display: inline-flex; align-items: center; gap: 6px;
    background: #3b82f6; color: #fff; border: none; border-radius: 8px;
    padding: 8px 16px; font-size: 13px; font-weight: 600;
    text-decoration: none; white-space: nowrap; cursor: pointer;
    transition: background 0.15s;
  }
  .surf-cam-btn:hover { background: #2563eb; }

  .footer { text-align: center; padding: 24px; font-size: 13px; color: var(--text-muted); }
  .footer a { color: #3b82f6; text-decoration: none; }
  .footer a:hover { text-decoration: underline; }

  /* Welcome modal — full page, normal scroll (no fixed positioning) */
  .modal-overlay {
    min-height: 100vh;
    background: #0d1117;
    padding: 20px;
    display: flex;
    align-items: flex-start;
    justify-content: center;
  }

  .modal-box {
    background: #1a1f2e;
    border: 1px solid rgba(255,255,255,0.12);
    border-radius: 18px;
    max-width: 500px;
    width: 100%;
    margin: 20px 0;
    box-shadow: 0 24px 60px rgba(0,0,0,0.7);
  }

  [data-theme="light"] .modal-box {
    background: #ffffff;
    border: 1px solid rgba(0,0,0,0.1);
  }

  .modal-header {
    background: #141824;
    padding: 28px 28px 20px;
    border-bottom: 1px solid rgba(255,255,255,0.08);
    text-align: center;
  }

  [data-theme="light"] .modal-header {
    background: #f0f4ff;
    border-bottom: 1px solid rgba(0,0,0,0.08);
  }

  .modal-logo {
    font-size: 11px;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: var(--text-muted);
    margin-bottom: 10px;
  }

  .modal-logo a {
    color: #3b82f6;
    text-decoration: none;
    font-weight: 500;
  }
  .modal-logo a:hover { text-decoration: underline; }

  .modal-title {
    font-family: 'Playfair Display', serif;
    font-size: 1.7rem;
    font-weight: 700;
    color: var(--text-primary);
    margin-bottom: 4px;
  }

  .modal-tagline {
    font-size: 13px;
    color: var(--text-muted);
    letter-spacing: 0.06em;
  }

  .modal-body { padding: 24px 28px; }

  .modal-intro {
    font-size: 14px;
    color: var(--text-secondary);
    line-height: 1.65;
    margin-bottom: 20px;
  }

  .modal-features {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    margin-bottom: 22px;
  }

  @media (max-width: 400px) {
    .modal-overlay { padding: 12px; }
    .modal-header { padding: 20px 16px 16px; }
    .modal-body { padding: 16px; }
    .modal-features { grid-template-columns: 1fr; }
    .modal-title { font-size: 1.4rem; }
  }

  .modal-feature {
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 10px;
    padding: 12px 14px;
    display: flex;
    align-items: flex-start;
    gap: 10px;
  }

  [data-theme="light"] .modal-feature {
    background: #f5f7ff;
    border: 1px solid rgba(0,0,0,0.07);
  }

  .modal-feature-icon { font-size: 18px; flex-shrink: 0; margin-top: 1px; }

  .modal-feature-text { font-size: 13px; color: var(--text-secondary); line-height: 1.4; }
  .modal-feature-text strong { color: var(--text-primary); font-weight: 500; display: block; margin-bottom: 2px; }

  .modal-free-box {
    background: rgba(0,112,186,0.08);
    border: 1px solid rgba(0,112,186,0.25);
    border-radius: 10px;
    padding: 16px;
    margin-bottom: 16px;
    text-align: center;
  }
  .modal-free-title { font-size: 15px; font-weight: 700; color: var(--text-primary); margin-bottom: 8px; }
  .modal-free-text  { font-size: 12px; color: var(--text-secondary); line-height: 1.6; margin: 0 0 12px; }
  .modal-free-hint  { font-size: 11px; color: var(--text-muted); margin: 10px 0 0; }
  .modal-wax-btn {
    display: inline-block; background: #0070ba; color: #fff;
    padding: 8px 20px; border-radius: 20px; font-size: 13px; font-weight: 700;
    text-decoration: none; transition: background 0.15s;
  }
  .modal-wax-btn:hover { background: #005ea6; }

  .modal-disclaimer {
    background: rgba(251,191,36,0.08);
    border: 1px solid rgba(251,191,36,0.25);
    border-radius: 10px;
    padding: 14px 16px;
    margin-bottom: 18px;
  }

  .modal-disclaimer-text {
    font-size: 12px;
    color: var(--text-secondary);
    line-height: 1.6;
    margin-bottom: 12px;
  }

  .modal-checkbox-row {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    cursor: pointer;
  }

  .modal-checkbox {
    width: 17px;
    height: 17px;
    border: 2px solid var(--border);
    border-radius: 4px;
    flex-shrink: 0;
    margin-top: 1px;
    background: var(--bg-card);
    cursor: pointer;
    accent-color: #3b82f6;
  }

  .modal-checkbox-label {
    font-size: 13px;
    color: var(--text-secondary);
    line-height: 1.5;
    cursor: pointer;
    user-select: none;
  }

  .modal-btn {
    width: 100%;
    padding: 13px;
    border-radius: 10px;
    border: none;
    font-family: 'DM Sans', sans-serif;
    font-size: 15px;
    font-weight: 500;
    cursor: pointer;
    transition: opacity 0.2s, transform 0.15s;
  }

  .modal-btn:enabled {
    background: #3b82f6;
    color: #fff;
  }
  .modal-btn:enabled:hover { opacity: 0.9; transform: translateY(-1px); }
  .modal-btn:disabled {
    background: var(--bg-inner);
    color: var(--text-muted);
    cursor: not-allowed;
  }

  .tide-row {
    display: flex; justify-content: space-between; align-items: center;
    background: var(--bg-inner2);
    border-radius: 7px;
    padding: 8px 10px;
    border: 1px solid var(--border);
    margin-bottom: 6px;
  }
  .tide-high { color: #3b82f6; font-size: 13px; font-weight: 600; }
  .tide-low  { color: #22c55e; font-size: 13px; font-weight: 600; }
  .tide-time-val { font-size: 13px; font-weight: 600; color: var(--text-primary); }
  .tide-ft   { font-size: 11px; color: var(--text-muted); }
`;

/* ─── Helpers ──────────────────────────────────────────────────────────── */
const getWetsuitRecommendation = (tempCelsius) => {
  if (tempCelsius >= 24) return { type: 'No Wetsuit',  icon: '🩳', description: 'Boardshorts or swimsuit is fine',   color: '#4ade80' };
  if (tempCelsius >= 21) return { type: 'Optional',    icon: '🌊', description: 'Rash guard or 0.5mm top for longer sessions', color: '#a3e635' };
  if (tempCelsius >= 19) return { type: 'Spring Suit', icon: '🏄‍♂️', description: '2mm spring suit or top',          color: '#facc15' };
  if (tempCelsius >= 17) return { type: 'Light Full',  icon: '🏄‍♀️', description: '2-3mm full suit',                color: '#fb923c' };
  if (tempCelsius >= 13) return { type: 'Full Suit',   icon: '🌊', description: '3-4mm full suit + booties',        color: '#f87171' };
  if (tempCelsius >= 8)  return { type: 'Heavy Suit',  icon: '❄️', description: '4-5mm suit + boots, gloves, hood', color: '#a78bfa' };
  return                        { type: 'Extra Thick', icon: '🧊', description: '6mm+ suit + boots, gloves, hood',  color: '#60a5fa' };
};

const PRESET_LOCATIONS = [
  // Virginia / OBX
  { name: 'Virginia Beach, VA',        lat: 36.8529,  lon: -75.9780  },
  { name: 'Nags Head, NC',             lat: 35.9575,  lon: -75.6249  },
  { name: 'Kitty Hawk, NC',            lat: 36.0640,  lon: -75.7068  },
  { name: 'Kill Devil Hills, NC',      lat: 35.9996,  lon: -75.6657  },
  { name: 'Avon, NC (OBX)',            lat: 35.3543,  lon: -75.5024  },
  { name: 'Salvo, NC (OBX)',           lat: 35.5368,  lon: -75.4668  },
  { name: 'Rodanthe, NC (OBX)',        lat: 35.5929,  lon: -75.4657  },
  { name: 'Buxton, NC (Cape Hatteras)',lat: 35.2693,  lon: -75.5274  },
  { name: 'Ocracoke, NC',              lat: 35.1132,  lon: -75.9813  },
  // East Coast
  { name: 'Wrightsville Beach, NC',    lat: 34.2087,  lon: -77.7966  },
  { name: 'Folly Beach, SC',           lat: 32.6551,  lon: -79.9403  },
  { name: 'Cocoa Beach, FL',           lat: 28.3200,  lon: -80.6076  },
  { name: 'New Smyrna Beach, FL',      lat: 29.0258,  lon: -80.9270  },
  // West Coast
  { name: 'Trestles, CA',              lat: 33.3853,  lon: -117.5939 },
  { name: 'Mavericks, CA',             lat: 37.4957,  lon: -122.4989 },
  // World
  { name: 'Pipeline, HI',              lat: 21.6654,  lon: -158.0525 },
  { name: 'Bondi Beach, Australia',    lat: -33.8915, lon: 151.2767  },
  { name: 'Uluwatu, Bali',             lat: -8.8128,  lon: 115.0890  },
  { name: 'Nazare, Portugal',          lat: 39.6079,  lon: -9.0705   },
  { name: 'Jeffreys Bay, S. Africa',   lat: -34.0467, lon: 24.9313   },
  { name: "Teahupo'o, Tahiti",         lat: -17.8516, lon: -149.2671 },
  { name: 'Mundaka, Spain',            lat: 43.4073,  lon: -2.6977   },
  { name: 'Bells Beach, Australia',    lat: -38.3704, lon: 144.2853  },
  { name: 'Hossegor, France',          lat: 43.6667,  lon: -1.4167   },
  { name: 'Supertubes, Portugal',      lat: 39.3436,  lon: -9.3669   },
  { name: 'Cloud Break, Fiji',         lat: -17.8667, lon: 177.2000  }
];

const TIMES_OF_DAY = [
  { hour: 6,  name: 'Dawn Patrol' },
  { hour: 9,  name: 'Morning'     },
  { hour: 12, name: 'Midday'      },
  { hour: 15, name: 'Afternoon'   },
  { hour: 18, name: 'Evening'     }
];

const getSurfCondition = (height, period) => {
  if (height < 0.3)                 return { text: 'Flat',  color: '#808080', level: 'Not surfable',      bgColor: '#f3f4f6', description: 'Better check out the coffee shop instead' };
  if (height < 0.6)                 return { text: 'Small', color: '#90EE90', level: 'Beginner Friendly', bgColor: '#ecfdf5', description: 'Perfect for learning' };
  // 0.6–1.2 m
  if (height < 1.2 && period >= 10) return { text: 'Epic',  color: '#16a34a', level: 'Advanced',          bgColor: '#86efac', description: 'Long period, punchy sets' };
  if (height < 1.2 && period >= 6)  return { text: 'Fun',   color: '#4ade80', level: 'All Levels',        bgColor: '#dcfce7', description: 'Good vibes only' };
  if (height < 1.2)                 return { text: 'Small', color: '#90EE90', level: 'Beginner Friendly', bgColor: '#ecfdf5', description: 'Short period, choppy' };
  // 1.2–2 m
  if (height < 2   && period >= 10) return { text: 'Epic',  color: '#16a34a', level: 'Advanced',          bgColor: '#86efac', description: 'Locals Only' };
  if (height < 2   && period >= 8)  return { text: 'Great', color: '#22c55e', level: 'Intermediate+',     bgColor: '#bbf7d0', description: 'Time to call in sick' };
  if (height < 2   && period >= 6)  return { text: 'Fun',   color: '#4ade80', level: 'All Levels',        bgColor: '#dcfce7', description: 'Good vibes only' };
  if (height < 2)                   return { text: 'Small', color: '#90EE90', level: 'Beginner Friendly', bgColor: '#ecfdf5', description: 'Big but choppy' };
  // 2 m+
  if (period >= 8)                  return { text: 'Epic',  color: '#16a34a', level: 'Advanced',          bgColor: '#86efac', description: 'Locals Only' };
  return                                   { text: 'Great', color: '#22c55e', level: 'Intermediate+',     bgColor: '#bbf7d0', description: 'Big and powerful' };
};

const getWindDirection = (degrees) => {
  const dirs = ['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSW','SW','WSW','W','WNW','NW','NNW'];
  return dirs[Math.round(degrees / 22.5) % 16];
};

const getWindCondition = (speed, direction, waveDirection) => {
  const mph = speed * 2.237;
  let diff = Math.abs(direction - waveDirection);
  if (diff > 180) diff = 360 - diff;
  const isOffshore  = diff > 90;
  const isSideshore = diff > 45 && diff <= 90;
  if (mph < 5)  return { condition: 'Calm',             quality: 'Good',      color: '#10b981', description: 'Light winds, clean conditions' };
  if (mph < 10) {
    if (isOffshore)  return { condition: 'Light Offshore', quality: 'Excellent', color: '#059669', description: 'Perfect grooming winds' };
    if (isSideshore) return { condition: 'Light Side',     quality: 'Good',      color: '#10b981', description: 'Manageable conditions' };
    return                  { condition: 'Light Onshore',  quality: 'Fair',      color: '#f59e0b', description: 'Slightly bumpy' };
  }
  if (mph < 20) {
    if (isOffshore) return { condition: 'Moderate Offshore', quality: 'Good', color: '#10b981', description: 'Good shape, some texture' };
    return                 { condition: 'Moderate Onshore',  quality: 'Poor', color: '#ef4444', description: 'Choppy and messy' };
  }
  return { condition: 'Strong', quality: 'Poor', color: '#dc2626', description: 'Blown out conditions' };
};

const getSurfConditionWithWind = (height, period, windSpeed, windDirection, waveDirection) => {
  const base = getSurfCondition(height, period);
  const wind = getWindCondition(windSpeed, windDirection, waveDirection);
  if (wind.quality === 'Poor' && base.text !== 'Flat')
    return { ...base, text: base.text + ' but Windy', description: 'Waves present but wind is hurting quality', color: '#f59e0b' };
  if (wind.quality === 'Excellent' && base.text !== 'Flat')
    return { ...base, text: base.text + '+', description: base.description + ' with clean offshore winds' };
  return base;
};

const getSurfRating = (height, period, windSpeed, windDirection, waveDirection) => {
  if (height < 0.3) return 0;
  const { text } = getSurfConditionWithWind(height, period, windSpeed, windDirection, waveDirection);
  if (text.startsWith('Flat'))  return 0;
  if (text.startsWith('Small')) return text.includes('+') ? 2 : 1;
  if (text.startsWith('Fun'))   return text.includes('Windy') ? 1 : text.includes('+') ? 3 : 2;
  if (text.startsWith('Great')) return text.includes('Windy') ? 2 : text.includes('+') ? 4 : 3;
  if (text.startsWith('Epic'))  return text.includes('Windy') ? 3 : text.includes('+') ? 5 : 4;
  return 2;
};

/* ─── Components ─────────────────────────────────────────────────────────── */
const LoadingWave = () => (
  <div className="loading-wrap">
    <div className="wave-loader">
      {[...Array(5)].map((_, i) => <div key={i} className="wave-bar" />)}
    </div>
    <p className="loading-text">Loading forecast…</p>
  </div>
);

const WaveHeightVisual = ({ height }) => {
  const count = Math.min(Math.ceil(height * 2), 5);
  return (
    <div className="wave-dots">
      {[...Array(count)].map((_, i) => (
        <div key={i} className="wave-dot" style={{ opacity: 0.4 + i * 0.15 }} />
      ))}
    </div>
  );
};

const SearchBar = ({ searchTerm, onSearchChange, onLocationSelect, favorites, onToggleFavorite }) => {
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const searchLocations = async (query) => {
    if (!query) { setSearchResults([]); return; }
    setIsSearching(true);
    try {
      // Curated beach/surf spots for US coastal states — returned instantly when
      // a state name is typed so users aren't left with "No locations found."
      const stateSpots = {
        'florida':        [{name:'Miami, Florida',lat:25.7617,lon:-80.1918},{name:'Cocoa Beach, Florida',lat:28.3200,lon:-80.6076},{name:'Daytona Beach, Florida',lat:29.2108,lon:-81.0228},{name:'Jacksonville Beach, Florida',lat:30.2919,lon:-81.3934},{name:'Fort Lauderdale, Florida',lat:26.1224,lon:-80.1373},{name:'Key West, Florida',lat:24.5551,lon:-81.7800},{name:'Panama City Beach, Florida',lat:30.1766,lon:-85.8055},{name:'St. Augustine, Florida',lat:29.8943,lon:-81.3145}],
        'california':     [{name:'Malibu, California',lat:34.0259,lon:-118.7798},{name:'Santa Cruz, California',lat:36.9741,lon:-122.0308},{name:'Huntington Beach, California',lat:33.6595,lon:-117.9988},{name:'San Diego, California',lat:32.7157,lon:-117.1611},{name:'Santa Barbara, California',lat:34.4208,lon:-119.6982},{name:'Half Moon Bay, California',lat:37.4636,lon:-122.4286},{name:'Encinitas, California',lat:33.0369,lon:-117.2920},{name:'Newport Beach, California',lat:33.6189,lon:-117.9289}],
        'hawaii':         [{name:'North Shore, Oahu, Hawaii',lat:21.6389,lon:-158.0626},{name:'Waikiki, Honolulu, Hawaii',lat:21.2793,lon:-157.8290},{name:'Hanalei, Kauai, Hawaii',lat:22.2037,lon:-159.5019},{name:'Maui, Hawaii',lat:20.7984,lon:-156.3319},{name:'Waimea Bay, Hawaii',lat:21.6427,lon:-158.0658},{name:'Kona, Hawaii',lat:19.6400,lon:-155.9969}],
        'north carolina': [{name:'Outer Banks, North Carolina',lat:35.5582,lon:-75.4665},{name:'Kitty Hawk, North Carolina',lat:36.0637,lon:-75.7085},{name:'Wrightsville Beach, North Carolina',lat:34.2087,lon:-77.7969},{name:'Cape Hatteras, North Carolina',lat:35.2269,lon:-75.5290},{name:'Carolina Beach, North Carolina',lat:34.0368,lon:-77.8994}],
        'south carolina': [{name:'Myrtle Beach, South Carolina',lat:33.6891,lon:-78.8867},{name:'Folly Beach, South Carolina',lat:32.6552,lon:-79.9403},{name:'Hilton Head, South Carolina',lat:32.2163,lon:-80.7526},{name:'Isle of Palms, South Carolina',lat:32.7874,lon:-79.7716}],
        'georgia':        [{name:'Tybee Island, Georgia',lat:31.9999,lon:-80.8451},{name:'St. Simons Island, Georgia',lat:31.1524,lon:-81.3673},{name:'Jekyll Island, Georgia',lat:31.0735,lon:-81.4182}],
        'new jersey':     [{name:'Asbury Park, New Jersey',lat:40.2204,lon:-74.0121},{name:'Cape May, New Jersey',lat:38.9351,lon:-74.9060},{name:'Belmar, New Jersey',lat:40.1776,lon:-74.0221},{name:'Long Branch, New Jersey',lat:40.2987,lon:-73.9872},{name:'Seaside Heights, New Jersey',lat:39.9429,lon:-74.0749}],
        'new york':       [{name:'Montauk, New York',lat:41.0365,lon:-71.9543},{name:'Rockaway Beach, New York',lat:40.5820,lon:-73.8165},{name:'Long Beach, New York',lat:40.5882,lon:-73.6582},{name:'Ditch Plains, New York',lat:41.0518,lon:-71.9468}],
        'virginia':       [{name:'Virginia Beach, Virginia',lat:36.8529,lon:-75.9780},{name:'Sandbridge, Virginia',lat:36.7051,lon:-75.9538}],
        'maryland':       [{name:'Ocean City, Maryland',lat:38.3365,lon:-75.0849},{name:'Assateague Island, Maryland',lat:38.0640,lon:-75.2096}],
        'delaware':       [{name:'Rehoboth Beach, Delaware',lat:38.7168,lon:-75.0757},{name:'Dewey Beach, Delaware',lat:38.6857,lon:-75.0763},{name:'Bethany Beach, Delaware',lat:38.5393,lon:-75.0568}],
        'massachusetts':  [{name:'Nantucket, Massachusetts',lat:41.2835,lon:-70.0995},{name:'Cape Cod, Massachusetts',lat:41.6688,lon:-70.2962},{name:'Rockport, Massachusetts',lat:42.6559,lon:-70.6201},{name:'Nahant, Massachusetts',lat:42.4259,lon:-70.9235}],
        'rhode island':   [{name:'Narragansett, Rhode Island',lat:41.4326,lon:-71.4573},{name:'Newport, Rhode Island',lat:41.4901,lon:-71.3128},{name:'Matunuck, Rhode Island',lat:41.3826,lon:-71.5365}],
        'connecticut':    [{name:'Westport, Connecticut',lat:41.1415,lon:-73.3579},{name:'Hammonasset, Connecticut',lat:41.2665,lon:-72.5636}],
        'maine':          [{name:'Old Orchard Beach, Maine',lat:43.5173,lon:-70.3762},{name:'Ogunquit, Maine',lat:43.2457,lon:-70.5975},{name:'Wells, Maine',lat:43.3223,lon:-70.5851}],
        'texas':          [{name:'South Padre Island, Texas',lat:26.1170,lon:-97.1683},{name:'Galveston, Texas',lat:29.3013,lon:-94.7977},{name:'Corpus Christi, Texas',lat:27.8006,lon:-97.3964},{name:'Port Aransas, Texas',lat:27.8336,lon:-97.0597}],
        'louisiana':      [{name:'Grand Isle, Louisiana',lat:29.2341,lon:-90.0032},{name:'Holly Beach, Louisiana',lat:29.7927,lon:-93.1152}],
        'mississippi':    [{name:'Biloxi, Mississippi',lat:30.3960,lon:-88.8853},{name:'Gulfport, Mississippi',lat:30.3674,lon:-89.0928},{name:'Ocean Springs, Mississippi',lat:30.4113,lon:-88.8309}],
        'alabama':        [{name:'Gulf Shores, Alabama',lat:30.2460,lon:-87.7006},{name:'Orange Beach, Alabama',lat:30.2955,lon:-87.5697}],
        'oregon':         [{name:'Cannon Beach, Oregon',lat:45.8918,lon:-123.9615},{name:'Seaside, Oregon',lat:45.9932,lon:-123.9232},{name:'Lincoln City, Oregon',lat:44.9582,lon:-124.0070},{name:'Bandon, Oregon',lat:43.1182,lon:-124.4085},{name:'Pacific City, Oregon',lat:45.2007,lon:-123.9607}],
        'washington':     [{name:'Westport, Washington',lat:46.8899,lon:-124.1046},{name:'Long Beach, Washington',lat:46.3518,lon:-124.0530},{name:'La Push, Washington',lat:47.9118,lon:-124.6368},{name:'Ocean Shores, Washington',lat:46.9754,lon:-124.1576}],
        'alaska':         [{name:'Kodiak, Alaska',lat:57.7900,lon:-152.4072},{name:'Sitka, Alaska',lat:57.0531,lon:-135.3300},{name:'Juneau, Alaska',lat:58.3005,lon:-134.4197}],
      };

      const key = query.trim().toLowerCase();
      if (stateSpots[key]) {
        setSearchResults(stateSpots[key]);
        setIsSearching(false);
        return;
      }

      // Block oversized/non-spot administrative divisions
      const blockedAddressTypes = new Set(['country','state','county','district','region','province','state_district']);
      const naturalCoastalTypes = new Set(['beach','bay','coastline','cape','cove','peninsula','headland','shoal','reef','lagoon','strait','sound','fjord','sea','ocean','water','wetland']);
      const coastalKeywords     = ['beach','surf','coast','shore','bay','cove','cape','pier','point','inlet','lagoon','island','atoll','reef','harbor','harbour','marina','jetty','dune','break'];
      const placeTypes          = new Set(['city','town','village','municipality','hamlet','suburb','quarter','borough','locality','island','islet','archipelago']);

      const applyFilter = (raw) => raw
        .filter(item => {
          if (item.class === 'boundary' && item.type === 'administrative' && blockedAddressTypes.has(item.addresstype)) return false;
          const name      = item.display_name.toLowerCase();
          const isNatural = (item.class === 'natural' || item.class === 'water') && naturalCoastalTypes.has(item.type);
          const hasKeyword= coastalKeywords.some(w => name.includes(w));
          const isPlace   = (item.class === 'place' && placeTypes.has(item.type)) ||
                            (item.class === 'boundary' && item.type === 'administrative');
          return isNatural || hasKeyword || isPlace;
        })
        .map(item => ({
          name: item.display_name.split(',').slice(0, 3).join(',').trim(),
          lat:  parseFloat(item.lat),
          lon:  parseFloat(item.lon)
        }));

      const nominatim = async (q) => {
        const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=20&addressdetails=1`);
        return applyFilter(await r.json());
      };

      const results = await nominatim(query);
      setSearchResults(results.slice(0, 8));
    } catch (e) { console.error(e); }
    setIsSearching(false);
  };

  useEffect(() => {
    const t = setTimeout(() => searchLocations(searchTerm), 500);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const filteredPresets = PRESET_LOCATIONS.filter(l => l.name.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div className="search-wrap">
      <Search className="search-icon" />
      <input
        className="search-input"
        type="text"
        placeholder="Search beaches worldwide..."
        value={searchTerm}
        onChange={e => onSearchChange(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setTimeout(() => setIsFocused(false), 150)}
      />
      {(searchTerm || isFocused) && (
        <div className="search-dropdown">
          {favorites.length > 0 && !searchTerm && (
            <>
              <div className="search-section-label">Favorites</div>
              {favorites.map(loc => (
                <div key={loc.name} className="search-item search-item-fav">
                  <button className="search-item-label" onClick={() => onLocationSelect(loc)}>
                    <Heart size={12} fill="currentColor" style={{ color: '#ef4444', marginRight: 6, flexShrink: 0 }} />
                    {loc.name}
                  </button>
                  <button className="search-item-remove" onClick={() => onToggleFavorite(loc)} title="Remove favorite">×</button>
                </div>
              ))}
            </>
          )}
          {filteredPresets.length > 0 && (
            <>
              <div className="search-section-label">Suggestions</div>
              {filteredPresets.map(loc => (
                <button key={loc.name} className="search-item" onClick={() => onLocationSelect(loc)}>{loc.name}</button>
              ))}
            </>
          )}
          {searchResults.length > 0 && (
            <>
              <div className="search-section-label">More Results</div>
              {searchResults
                .filter(r => !filteredPresets.some(p => p.name.toLowerCase() === r.name.toLowerCase()))
                .map((loc, i) => (
                  <button key={i} className="search-item" onClick={() => onLocationSelect(loc)}>{loc.name}</button>
                ))
              }
            </>
          )}
          {isSearching && (
            <div style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text-muted)' }}>Searching…</div>
          )}
          {!isSearching && searchTerm && filteredPresets.length === 0 && searchResults.length === 0 && (
            <div style={{ padding: '12px', fontSize: 13, color: 'var(--text-muted)', textAlign: 'center' }}>No locations found</div>
          )}
        </div>
      )}
    </div>
  );
};

const WaterTemperatureCard = ({ waterTemp }) => {
  const tempF      = waterTemp * 9/5 + 32;
  const wetsuitRec = getWetsuitRecommendation(waterTemp);
  return (
    <div className="card" style={{ borderTop: `3px solid ${wetsuitRec.color}` }}>
      <div className="card-inner">
        <div className="card-label">Water Temperature</div>
        <span className="card-icon">{wetsuitRec.icon}</span>
        <div className="card-verdict" style={{ color: wetsuitRec.color }}>
          {tempF.toFixed(1)}°F / {waterTemp.toFixed(1)}°C
        </div>
        <div className="card-desc">Feels {tempF < 60 ? 'cold' : tempF < 72 ? 'cool' : 'warm'}</div>
        <div className="inner-box" style={{ textAlign: 'center' }}>
          <div className="stat-value" style={{ color: wetsuitRec.color }}>{wetsuitRec.type}</div>
          <div className="stat-sub">{wetsuitRec.description}</div>
        </div>
      </div>
    </div>
  );
};

const TideCard = ({ tides, selectedDate, isToday }) => {
  if (!tides || tides.length === 0) return (
    <div className="card">
      <div className="card-inner">
        <div className="card-label">Tides</div>
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>🌊</div>
          <div style={{ fontSize: 14, color: 'var(--text-secondary)', fontWeight: 600 }}>Tide Data</div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>Not available for this location</div>
        </div>
      </div>
    </div>
  );

  const now      = new Date();
  const nextTide = isToday ? tides.find(t => new Date(t.time) > now) : null;
  const dayName  = isToday ? "Today's" : selectedDate.toLocaleDateString('en-US', { weekday: 'long' }) + "'s";

  return (
    <div className="card" style={{ borderTop: '3px solid #3b82f6' }}>
      <div className="card-inner">
        <div className="card-label">{dayName} Tides</div>
        <div style={{ textAlign: 'center', fontSize: 36, marginBottom: 8 }}>🌊</div>
        {nextTide && (
          <div className="inner-box" style={{ textAlign: 'center', marginBottom: 12 }}>
            <div className="stat-label">Next Tide</div>
            <div className="stat-value" style={{ color: '#3b82f6' }}>
              {nextTide.type} at {new Date(nextTide.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
            <div className="stat-sub">{nextTide.height.toFixed(1)} ft</div>
          </div>
        )}
        {tides.slice(0, 4).map((tide, i) => (
          <div key={i} className="tide-row">
            <span className={tide.type === 'High' ? 'tide-high' : 'tide-low'}>{tide.type}</span>
            <div style={{ textAlign: 'right' }}>
              <div className="tide-time-val">
                {new Date(tide.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
              <div className="tide-ft">{tide.height.toFixed(1)} ft</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const SurfDayOverview = ({ forecast, selectedDay, dayGroups }) => {
  if (!forecast || !dayGroups || !dayGroups.length) return null;
  const dayData = dayGroups[selectedDay];
  if (!dayData) return null;

  const fc  = dayData.forecasts;
  const avg = (key) => fc.reduce((s, f) => s + (f[key] || 0), 0) / fc.length;

  const avgHeight    = avg('waveHeight');
  const avgPeriod    = avg('wavePeriod');
  const avgWaterTemp = avg('waterTemp');
  const avgWindSpeed = avg('windSpeed');
  const avgWindDir   = avg('windDirection');
  const avgWaveDir   = avg('waveDirection');

  const baseCondition    = getSurfCondition(avgHeight, avgPeriod);
  const windCondition    = getWindCondition(avgWindSpeed, avgWindDir, avgWaveDir);
  const overallCondition = getSurfConditionWithWind(avgHeight, avgPeriod, avgWindSpeed, avgWindDir, avgWaveDir);

  const isGoodDay    = avgHeight > 0.6 && avgPeriod > 6 && windCondition.quality !== 'Poor';
  const selectedDate = dayData.date;
  const today        = new Date();
  const isToday      = selectedDate.toDateString() === today.toDateString();
  const dayName      = isToday ? 'Today' : selectedDate.toLocaleDateString('en-US', { weekday: 'long' });
  const windSpeedMph = (avgWindSpeed * 2.237).toFixed(0);
  const windDir      = getWindDirection(avgWindDir);
  const dayTides     = forecast.tides
    ? forecast.tides.filter(t => new Date(t.time).toDateString() === selectedDate.toDateString())
    : [];

  return (
    <div className="overview-section">
      <div className="day-heading">
        <strong>{dayName}</strong> — {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
      </div>
      <div className="overview-grid">
        <div className="card" style={{ borderTop: `3px solid ${overallCondition.color}` }}>
          <div className="card-inner">
            <div className="card-label">Surf Conditions</div>
            <span className="card-icon">{isGoodDay ? '🏄‍♂️' : '☕'}</span>
            <div className="card-verdict" style={{ color: isGoodDay ? '#059669' : '#9a3412' }}>
              {isGoodDay ? 'Great Day to Surf!' : `Maybe Skip ${isToday ? 'Today' : 'This Day'}`}
            </div>
            <div className="card-desc">{overallCondition.description}</div>
            <div className="inner-box">
              <div className="stats-2col">
                <div>
                  <div className="stat-label">Waves</div>
                  <div className="stat-value" style={{ color: '#3b82f6' }}>
                    {avgHeight.toFixed(1)}m ({(avgHeight * 3.28084).toFixed(1)}ft)
                  </div>
                  <div className="stat-sub" style={{ color: baseCondition.color }}>{baseCondition.text}</div>
                </div>
                <div>
                  <div className="stat-label">Wind</div>
                  <div className="stat-value" style={{ color: windCondition.color }}>
                    {windDir} {windSpeedMph}mph
                  </div>
                  <div className="stat-sub" style={{ color: windCondition.color }}>{windCondition.condition}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <WaterTemperatureCard waterTemp={avgWaterTemp} />
        <TideCard tides={dayTides} selectedDate={selectedDate} isToday={isToday} />
      </div>
    </div>
  );
};

const DayTab = ({ date, isSelected, onClick }) => {
  const today   = new Date();
  const isToday = date.toDateString() === today.toDateString();
  return (
    <button className={`day-tab ${isSelected ? 'active' : ''}`} onClick={onClick}>
      <div className="tab-day">{isToday ? 'Today' : date.toLocaleDateString('en-US', { weekday: 'short' })}</div>
      <div className="tab-date">{date.getDate()} {date.toLocaleDateString('en-US', { month: 'short' })}</div>
    </button>
  );
};

const StarRating = ({ rating, color }) => (
  <div style={{ fontSize: '1.1rem', letterSpacing: '2px', margin: '4px 0' }}>
    {[1,2,3,4,5].map(i => (
      <span key={i} style={{ color: i <= rating ? color : '#d1d5db' }}>★</span>
    ))}
  </div>
);

const TimeCard = ({ time, waveHeight, wavePeriod, waterTemp, windSpeed, windDirection, waveDirection }) => {
  const condition     = getSurfConditionWithWind(waveHeight, wavePeriod, windSpeed, windDirection, waveDirection);
  const windCondition = getWindCondition(windSpeed, windDirection, waveDirection);
  const rating        = getSurfRating(waveHeight, wavePeriod, windSpeed, windDirection, waveDirection);
  const date          = new Date(time);
  const timeOfDay     = TIMES_OF_DAY.find(t => t.hour === date.getHours()) || { name: 'Night' };
  const tempF         = (waterTemp * 9/5 + 32);
  const windSpeedMph  = (windSpeed * 2.237).toFixed(0);
  const windDir       = getWindDirection(windDirection);
  const suit          = getWetsuitRecommendation(waterTemp);

  return (
    <div className="time-card" style={{ backgroundColor: condition.bgColor, borderTop: `3px solid ${condition.color}` }}>
      <div className="time-card-inner">
        <div className="tc-time-label">{timeOfDay.name}</div>

        <WaveHeightVisual height={waveHeight} />

        <div className="tc-condition" style={{ color: condition.color }}>{condition.text}</div>
        <StarRating rating={rating} color={condition.color} />
        <div className="tc-desc">{condition.description}</div>

        <div className="tc-inner-box">
          <div className="tc-stats-grid">
            <div>
              <div className="tc-stat-label">Height</div>
              <div className="tc-stat-value" style={{ color: '#3b82f6' }}>{waveHeight.toFixed(1)}m</div>
              <div className="tc-stat-sub">({(waveHeight * 3.28084).toFixed(1)}ft)</div>
            </div>
            <div>
              <div className="tc-stat-label">Period</div>
              <div className="tc-stat-value" style={{ color: '#22c55e' }}>{wavePeriod.toFixed(1)}s</div>
              <div className="tc-stat-sub">{wavePeriod > 10 ? 'Strong' : 'Moderate'}</div>
            </div>
          </div>
          <div className="divider" />
          <div className="tc-stats-grid">
            <div>
              <div className="tc-stat-label">Wind</div>
              <div className="tc-stat-value" style={{ color: windCondition.color }}>{windDir} {windSpeedMph}mph</div>
              <div className="tc-stat-sub">{windCondition.condition}</div>
            </div>
            <div>
              <div className="tc-stat-label">Water</div>
              <div className="tc-stat-value" style={{ color: '#a855f7' }}>{tempF.toFixed(1)}°F</div>
              <div className="tc-stat-sub">{suit.type}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Surf Cam ───────────────────────────────────────────────────────────── */
const SURF_CAMS = {
  // Outer Banks, NC
  'Nags Head, NC':              'https://www.outerbanks.org/webcams/',
  'Kitty Hawk, NC':             'https://www.outerbanks.org/webcams/',
  'Kill Devil Hills, NC':       'https://www.outerbanks.org/webcams/',
  'Avon, NC (OBX)':             'https://www.outerbanks.org/webcams/',
  'Salvo, NC (OBX)':            'https://www.outerbanks.org/webcams/',
  'Rodanthe, NC (OBX)':         'https://www.outerbanks.org/webcams/',
  'Buxton, NC (Cape Hatteras)': 'https://www.outerbanks.org/webcams/',
  'Ocracoke, NC':               'https://www.outerbanks.org/webcams/',
  // Virginia
  'Virginia Beach, VA':         'https://www.surfchex.com/cams/virginia-beach-web-cam/',
  // Carolinas
  'Wrightsville Beach, NC':     'https://wblivesurf.com/',
  'Folly Beach, SC':            'https://www.tidesfollybeach.com/surf-cam/',
  // Florida
  'Cocoa Beach, FL':            'https://www.surfguru.com/cocoa-beach-pier-surf-report',
  'New Smyrna Beach, FL':       'https://www.livebeaches.com/city/new-smyrna-beach-fl/',
  // California
  'Trestles, CA':               'https://www.surf-forecast.com/breaks/Trestles_Lowers/webcams/latest',
  'Mavericks, CA':              'https://worldcam.eu/webcams/north-america/california-usa/19560-half-moon-bay-pillar-point-harbor',
  // Hawaii
  'Pipeline, HI':               'https://explore.org/livecams/hawaii/hawaii-pipeline-cam',
  // Australia
  'Bondi Beach, Australia':     'https://bondisurfclub.com/bondi-surf-cam/',
  'Bells Beach, Australia':     'https://www.surfline.com/surf-report/bells-beach/584204204e65fad6a77099c7',
  // Indonesia
  'Uluwatu, Bali':              'https://www.surf-forecast.com/breaks/Uluwatu/webcams/latest',
  // Portugal
  'Nazare, Portugal':           'https://nazarewaves.com/en/webcams',
  'Supertubes, Portugal':       'https://beachcam.meo.pt/livecams/peniche-supertubos/',
  // France
  'Hossegor, France':           'https://quiksilver.co.uk/surf/webcams/hossegor.html',
  // Spain
  'Mundaka, Spain':             'https://surfepico.es/webcam/webcam-mundaka/',
  // South Africa
  'Jeffreys Bay, S. Africa':    'https://www.webcamtaxi.com/en/south-africa/eastern-cape/jeffreysbay-supertubes-cam.html',
  // Tahiti
  "Teahupo'o, Tahiti":          'https://www.windy.com/webcams/1398878421',
  // Fiji
  'Cloud Break, Fiji':          'https://www.swellnet.com/reports/south-pacific/fiji/cloudbreak',
};

const SurfCamCard = ({ location }) => {
  const camUrl = SURF_CAMS[location.name];
  if (!camUrl) return null;
  return (
    <div className="surf-cam-card">
      <div className="surf-cam-inner">
        <div className="surf-cam-info">
          <div className="surf-cam-icon">📹</div>
          <div>
            <div className="surf-cam-title">Live Cam</div>
            <div className="surf-cam-sub">{location.name}</div>
          </div>
        </div>
        <a href={camUrl} target="_blank" rel="noopener noreferrer" className="surf-cam-btn">
          Watch Live →
        </a>
      </div>
    </div>
  );
};

/* ─── Welcome Modal ──────────────────────────────────────────────────────── */
const WelcomeModal = ({ onAccept }) => {
  const [agreed, setAgreed] = useState(false);

  return (
    <div className="modal-overlay">
      <div className="modal-box">
        <div className="modal-header">
          <div className="modal-logo">
            A tool by <a href="https://www.builtbywelch.com" target="_blank" rel="noopener noreferrer">BuiltByWelch</a>
          </div>
          <div className="modal-title">Surf Forecast</div>
          <div className="modal-tagline">Live conditions · 7-day outlook · Worldwide</div>
        </div>

        <div className="modal-body">
          <p className="modal-intro">
            Get real-time surf conditions for beaches around the world. Search any coastal location
            or pick from popular surf spots — no account required.
          </p>

          <div className="modal-features">
            <div className="modal-feature">
              <span className="modal-feature-icon">🌊</span>
              <div className="modal-feature-text">
                <strong>Wave Forecast</strong>
                Height, period &amp; star quality rating updated hourly
              </div>
            </div>
            <div className="modal-feature">
              <span className="modal-feature-icon">💨</span>
              <div className="modal-feature-text">
                <strong>Wind Conditions</strong>
                Offshore vs. onshore wind quality assessment
              </div>
            </div>
            <div className="modal-feature">
              <span className="modal-feature-icon">🌡️</span>
              <div className="modal-feature-text">
                <strong>Water Temp</strong>
                With wetsuit recommendations per session
              </div>
            </div>
            <div className="modal-feature">
              <span className="modal-feature-icon">🌑</span>
              <div className="modal-feature-text">
                <strong>Tide Tables</strong>
                High &amp; low tides for US coastal locations
              </div>
            </div>
            <div className="modal-feature">
              <span className="modal-feature-icon">📹</span>
              <div className="modal-feature-text">
                <strong>Live Surf Cams</strong>
                Direct links to live cams for popular spots worldwide
              </div>
            </div>
            <div className="modal-feature">
              <span className="modal-feature-icon">❤️</span>
              <div className="modal-feature-text">
                <strong>Favorites</strong>
                Save your go-to spots for quick access
              </div>
            </div>
          </div>

          <div className="modal-free-box">
            <div className="modal-free-title">🤙 100% Free — No Account Required</div>
            <p className="modal-free-text">
              This app is completely free to use. No sign-up, no ads, no paywalls.
              If it helps you score good waves, consider buying me some surf wax —
              it keeps the stoke alive and the servers running.
            </p>
            <a
              href="https://paypal.me/builtbywelch"
              target="_blank"
              rel="noopener noreferrer"
              className="modal-wax-btn"
            >
              🤙 Buy Me Some Surf Wax
            </a>
            <p className="modal-free-hint">
              You can also find the donate button in the <strong>top-left corner</strong> of the app anytime.
            </p>
          </div>

          <div className="modal-disclaimer">
            <p className="modal-disclaimer-text">
              <strong style={{ color: 'var(--text-primary)' }}>Educational &amp; Informational Use Only.</strong><br />
              Surf Forecast is provided for general informational purposes only. Forecasts are sourced
              from third-party APIs and may not reflect actual ocean conditions. Always assess conditions
              in person before entering the water. BuiltByWelch is not responsible for decisions made
              based on this data. Ocean activities carry inherent risk — surf within your ability level
              and never surf alone.
            </p>
            <label className="modal-checkbox-row">
              <input
                type="checkbox"
                className="modal-checkbox"
                checked={agreed}
                onChange={e => setAgreed(e.target.checked)}
              />
              <span className="modal-checkbox-label">
                I understand this forecast is for informational purposes only and I assume
                full responsibility for my own safety in the water.
              </span>
            </label>
          </div>

          <button className="modal-btn" disabled={!agreed} onClick={onAccept}>
            {agreed ? 'Get the Forecast →' : 'Please read and accept above'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── App ────────────────────────────────────────────────────────────────── */
const App = () => {
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  const [theme, setTheme]                       = useState(prefersDark ? 'dark' : 'light');
  const [showModal, setShowModal]               = useState(() => !localStorage.getItem('surf_accepted'));
  const [forecast, setForecast]                 = useState(null);
  const [loading, setLoading]                   = useState(false);
  const [error, setError]                       = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(PRESET_LOCATIONS[0]);
  const [searchTerm, setSearchTerm]             = useState('');
  const [selectedDay, setSelectedDay]           = useState(0);
  const [favorites, setFavorites]               = useState(() => JSON.parse(localStorage.getItem('surf_favorites') || '[]'));

  useEffect(() => { localStorage.setItem('surf_favorites', JSON.stringify(favorites)); }, [favorites]);

  const toggleFavorite = (loc) => {
    setFavorites(prev =>
      prev.some(f => f.name === loc.name)
        ? prev.filter(f => f.name !== loc.name)
        : [...prev, loc]
    );
  };

  const isFavorite = favorites.some(f => f.name === selectedLocation.name);

  // Follow system preference changes
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setTheme(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    const fetchForecast = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getSurfForecast(selectedLocation.lat, selectedLocation.lon);
        await new Promise(r => setTimeout(r, 2500));
        setForecast(data);
        setSelectedDay(0);
      } catch (err) {
        setError('Error fetching surf forecast.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchForecast();
  }, [selectedLocation]);

  const handleAccept = () => {
    localStorage.setItem('surf_accepted', '1');
    setShowModal(false);
  };

  const handleLocationSelect = (loc) => { setSelectedLocation(loc); setSearchTerm(''); };

  const groupForecastsByDay = () => {
    if (!forecast) return [];
    const groups = [];
    let currentDay = null, currentGroup = [];
    forecast.hourly.time.forEach((time, index) => {
      const date     = new Date(time);
      const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      if (!currentDay || dayStart.getTime() !== currentDay.getTime()) {
        if (currentGroup.length) groups.push({ date: currentDay, forecasts: currentGroup });
        currentDay   = dayStart;
        currentGroup = [];
      }
      if (TIMES_OF_DAY.some(t => t.hour === date.getHours())) {
        currentGroup.push({
          time,
          waveHeight:    forecast.hourly.wave_height[index],
          wavePeriod:    forecast.hourly.wave_period[index],
          waterTemp:     forecast.hourly.water_temperature[index],
          windSpeed:     forecast.hourly.wind_speed_10m[index],
          windDirection: forecast.hourly.wind_direction_10m[index],
          waveDirection: forecast.hourly.wave_direction[index]
        });
      }
    });
    if (currentGroup.length) groups.push({ date: currentDay, forecasts: currentGroup });
    return groups;
  };

  const dayGroups = groupForecastsByDay();

  if (showModal) {
    return (
      <>
        <style>{themeStyles}</style>
        <WelcomeModal onAccept={handleAccept} />
      </>
    );
  }

  return (
    <>
      <style>{themeStyles}</style>
      <div className="surf-app" data-theme={theme}>

        <div className="surf-header">
          <a
            href="https://paypal.me/builtbywelch"
            target="_blank"
            rel="noopener noreferrer"
            style={{ position: 'absolute', top: 16, left: 16, background: '#0070ba', color: '#fff', padding: '7px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700, textDecoration: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.18)', whiteSpace: 'nowrap' }}
          >
            🤙 Buy Me Some Surf Wax
          </a>
          <button
            className="theme-toggle"
            onClick={() => setTheme(t => t === 'dark' ? 'light' : 'dark')}
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <h1 className="surf-title">Surf Forecast</h1>
          <p className="surf-subtitle">Live conditions · 7-day outlook</p>
          <SearchBar searchTerm={searchTerm} onSearchChange={setSearchTerm} onLocationSelect={handleLocationSelect} favorites={favorites} onToggleFavorite={toggleFavorite} />
        </div>

        <div className="location-bar">
          <div className="location-name-row">
            <div className="location-name">{selectedLocation.name}</div>
            <button onClick={() => toggleFavorite(selectedLocation)} title={isFavorite ? 'Remove from favorites' : 'Add to favorites'} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1, color: isFavorite ? '#ef4444' : 'var(--text-muted)' }}>
              <Heart size={18} fill={isFavorite ? '#ef4444' : 'none'} stroke="currentColor" />
            </button>
          </div>
          <div className="location-coords">
            {Math.abs(selectedLocation.lat).toFixed(4)}°{selectedLocation.lat >= 0 ? 'N' : 'S'}&nbsp;
            {Math.abs(selectedLocation.lon).toFixed(4)}°{selectedLocation.lon >= 0 ? 'E' : 'W'}
          </div>
        </div>

        <div className="main-content">
          {loading && <LoadingWave />}
          {error && <div className="error-box">{error}</div>}

          {forecast && !loading && (
            <>
              <SurfDayOverview forecast={forecast} selectedDay={selectedDay} dayGroups={dayGroups} />

              <SurfCamCard location={selectedLocation} />

              <div className="day-tabs">
                {dayGroups.map((g, i) => (
                  <DayTab key={i} date={g.date} isSelected={selectedDay === i} onClick={() => setSelectedDay(i)} />
                ))}
              </div>

              <div className="time-grid">
                {dayGroups[selectedDay]?.forecasts.map((d, i) => (
                  <TimeCard
                    key={i}
                    time={d.time}
                    waveHeight={d.waveHeight}
                    wavePeriod={d.wavePeriod}
                    waterTemp={d.waterTemp}
                    windSpeed={d.windSpeed}
                    windDirection={d.windDirection}
                    waveDirection={d.waveDirection}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        <div className="footer">
          Built by <a href="https://www.builtbywelch.com" target="_blank" rel="noopener noreferrer">Welch</a>
        </div>
      </div>
    </>
  );
};

export default App;
